import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { create } from 'zustand';

/**
 * Tab-Leiste unter der oberen Leiste (ADR 0111, Variante A „klassisch").
 *
 * Die Adresse bleibt die Wahrheit: Der aktive Tab ist immer die Seite, die der
 * Router gerade zeigt. Navigation im aktuellen Tab schreibt seine Adresse um,
 * ein Klick auf einen Tab ist eine ganz normale Navigation zu dessen Adresse.
 * Eine Seite (derselbe Pfad) ist nie zweimal offen — wer sie ansteuert, landet
 * im vorhandenen Tab.
 *
 * Gespeichert wird je Benutzer und Gerät im localStorage, wie „Zuletzt
 * geöffnet" (`recentPlaces.ts`): eine Bequemlichkeit, keine Datenhaltung.
 * Alles Fachliche steht in reinen Funktionen, damit es ohne Router testbar ist.
 */
export interface OpenTab {
  id: string;
  /** Adresse inkl. Query und Anker — dorthin kehrt der Tab zurück. */
  path: string;
  isPinned: boolean;
  /** Titel, den die Seite selbst meldet (Brotkrumen, `useTabTitle`). */
  title?: string;
  /** Hauptüberschrift der Seite — Ersatz, wenn sie keinen Titel meldet. */
  heading?: string;
}

export interface TabsSnapshot {
  tabs: OpenTab[];
  activeId: string | null;
  /** Zuletzt geschlossene Adressen, neueste zuerst (Alt+⇧+T). */
  closedPaths: string[];
}

export type CreateTabId = () => string;

export const HOME_PATH = '/';
export const CLOSED_TABS_LIMIT = 10;
/** Ab hier zeigt die App die Leiste (`tablet`, 744 px) — darunter übernimmt die MobileDock. */
export const TAB_BAR_MEDIA_QUERY = '(min-width: 744px)';

/**
 * Seiten ohne eigenen Tab: Vollbild-Ansichten außerhalb der App-Hülle (Editor,
 * PDF, Prüf- und Pickmasken) und reine Weiterleitungen dorthin. Ein ⌘-Klick
 * darauf bleibt ein Browser-Tab.
 *
 * Belege selbst (`/verkauf/:id`, `/beschaffung/:id`) haben seit 2026-09-30 einen
 * Tab: Der Editor öffnet dort in der App-Hülle, nur `…/editor` ist das Vollbild
 * (`features/documents/editorDisplayMode.ts`).
 */
const NOT_TABBABLE: RegExp[] = [
  /^\/api\//,
  /^\/verkauf\/[^/]+\/(editor|pdf)$/,
  /^\/beschaffung\/[^/]+\/(editor|pdf)$/,
  /^\/wareneingang\/[^/]+\/pruefen$/,
  /^\/kommissionierung\/[^/]+\/picken$/,
  /^\/montage\/[^/]+\/bericht$/,
  /^\/projekte\/[^/]+\/protokolle\/[^/]+$/,
  /^\/projekte\/[^/]+\/material-vollbild$/,
  /^\/referenzen\/karte\/vollbild$/,
  /^\/zeiterfassung\/protokoll$/,
];

/** Welcher Tab gehört zu dieser Adresse? Der Pfad — Filter und Anker ändern die Seite nicht. */
export function tabKey(path: string): string {
  const pathname = path.split(/[?#]/)[0] || HOME_PATH;
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

export function isTabbablePath(pathname: string): boolean {
  const key = tabKey(pathname);
  return !NOT_TABBABLE.some((pattern) => pattern.test(key));
}

/**
 * Ziel eines Links als Tab-Adresse, oder `null`, wenn der Link kein ProERP-Tab
 * werden soll: fremde Herkunft, eigenes Ziel-Fenster, Download, Vollbild-Seite
 * oder ausdrücklich abgewählt (`data-no-app-tab`).
 */
export function linkTabPath(anchor: HTMLAnchorElement, origin: string): string | null {
  const target = anchor.getAttribute('target');
  if (target && target !== '_self') return null;
  if (anchor.hasAttribute('download') || anchor.closest('[data-no-app-tab]')) return null;
  let url: URL;
  try {
    url = new URL(anchor.href);
  } catch {
    return null;
  }
  if (url.origin !== origin || !isTabbablePath(url.pathname)) return null;
  return url.pathname + url.search + url.hash;
}

export function createDefaultSnapshot(createId: CreateTabId): TabsSnapshot {
  const home: OpenTab = { id: createId(), path: HOME_PATH, isPinned: true };
  return { tabs: [home], activeId: home.id, closedPaths: [] };
}

/** Angeheftete zuerst, sonst bleibt die Reihenfolge. */
function pinnedFirst(tabs: OpenTab[]): OpenTab[] {
  return [...tabs.filter((tab) => tab.isPinned), ...tabs.filter((tab) => !tab.isPinned)];
}

function insertAt(tabs: OpenTab[], index: number, tab: OpenTab): OpenTab[] {
  return pinnedFirst([...tabs.slice(0, index), tab, ...tabs.slice(index)]);
}

function findByKey(snapshot: TabsSnapshot, path: string): OpenTab | undefined {
  const key = tabKey(path);
  return snapshot.tabs.find((tab) => tabKey(tab.path) === key);
}

function rememberClosed(closedPaths: string[], path: string): string[] {
  const key = tabKey(path);
  return [path, ...closedPaths.filter((entry) => tabKey(entry) !== key)].slice(0, CLOSED_TABS_LIMIT);
}

/**
 * Der Router zeigt `path` — Tabs nachziehen. Offen → dorthin wechseln (die
 * Adresse inkl. Filter übernehmen). Sonst navigiert der aktuelle Tab; ist er
 * angeheftet, bekommt die Seite einen neuen Tab direkt dahinter. Ein
 * Direktaufruf (`isInitial`: erste Adresse nach dem Laden der App) überschreibt
 * keinen gemerkten Tab, sondern kommt als eigener ans Ende.
 */
export function syncLocation(
  snapshot: TabsSnapshot,
  path: string,
  options: { isInitial: boolean; createId: CreateTabId },
): TabsSnapshot {
  const existing = findByKey(snapshot, path);
  if (existing) {
    if (existing.id === snapshot.activeId && existing.path === path) return snapshot;
    return {
      ...snapshot,
      activeId: existing.id,
      tabs: snapshot.tabs.map((tab) => (tab.id === existing.id ? { ...tab, path } : tab)),
    };
  }
  const active = snapshot.tabs.find((tab) => tab.id === snapshot.activeId);
  if (options.isInitial || !active || active.isPinned) {
    const tab: OpenTab = { id: options.createId(), path, isPinned: false };
    const index = options.isInitial || !active ? snapshot.tabs.length : snapshot.tabs.indexOf(active) + 1;
    return { ...snapshot, tabs: insertAt(snapshot.tabs, index, tab), activeId: tab.id };
  }
  return {
    ...snapshot,
    tabs: snapshot.tabs.map((tab) => (tab.id === active.id ? { id: tab.id, path, isPinned: tab.isPinned } : tab)),
  };
}

/**
 * Seite in einem neuen Tab öffnen — hinter `afterId` (sonst hinter dem aktiven).
 * Ist sie schon offen, bleibt es bei ihrem Tab (`isNew: false`).
 */
export function openTab(
  snapshot: TabsSnapshot,
  path: string,
  options: { activate: boolean; afterId?: string; createId: CreateTabId },
): { snapshot: TabsSnapshot; tabId: string; isNew: boolean } {
  const existing = findByKey(snapshot, path);
  if (existing) {
    const next = options.activate && snapshot.activeId !== existing.id ? { ...snapshot, activeId: existing.id } : snapshot;
    return { snapshot: next, tabId: existing.id, isNew: false };
  }
  const tab: OpenTab = { id: options.createId(), path, isPinned: false };
  const anchorIndex = snapshot.tabs.findIndex((entry) => entry.id === (options.afterId ?? snapshot.activeId));
  const index = anchorIndex >= 0 ? anchorIndex + 1 : snapshot.tabs.length;
  return {
    snapshot: { ...snapshot, tabs: insertAt(snapshot.tabs, index, tab), activeId: options.activate ? tab.id : snapshot.activeId },
    tabId: tab.id,
    isNew: true,
  };
}

/** Tab schließen; war er aktiv, übernimmt der rechte Nachbar (am Ende der linke). */
export function closeTab(snapshot: TabsSnapshot, id: string, createId: CreateTabId): TabsSnapshot {
  const index = snapshot.tabs.findIndex((tab) => tab.id === id);
  if (index < 0) return snapshot;
  const closedPaths = rememberClosed(snapshot.closedPaths, snapshot.tabs[index].path);
  const tabs = snapshot.tabs.filter((tab) => tab.id !== id);
  if (tabs.length === 0) return { ...createDefaultSnapshot(createId), closedPaths };
  const activeId = snapshot.activeId === id ? (tabs[index] ?? tabs[index - 1]).id : snapshot.activeId;
  return { tabs, activeId, closedPaths };
}

function closeWhere(snapshot: TabsSnapshot, keepId: string, shouldClose: (tab: OpenTab, index: number) => boolean): TabsSnapshot {
  let closedPaths = snapshot.closedPaths;
  const tabs = snapshot.tabs.filter((tab, index) => {
    if (tab.id === keepId || tab.isPinned || !shouldClose(tab, index)) return true;
    closedPaths = rememberClosed(closedPaths, tab.path);
    return false;
  });
  const activeId = tabs.some((tab) => tab.id === snapshot.activeId) ? snapshot.activeId : keepId;
  return { tabs, activeId, closedPaths };
}

/** Alle außer diesem schließen — angeheftete bleiben; der gewählte wird aktiv. */
export function closeOtherTabs(snapshot: TabsSnapshot, id: string): TabsSnapshot {
  return { ...closeWhere(snapshot, id, () => true), activeId: id };
}

/** Alle rechts davon schließen (angeheftete bleiben). */
export function closeTabsToRight(snapshot: TabsSnapshot, id: string): TabsSnapshot {
  const index = snapshot.tabs.findIndex((tab) => tab.id === id);
  if (index < 0) return snapshot;
  return closeWhere(snapshot, id, (_tab, tabIndex) => tabIndex > index);
}

/** Zuletzt geschlossenen Tab zurückholen und aktivieren (`null`, wenn es keinen gibt). */
export function reopenClosedTab(snapshot: TabsSnapshot, createId: CreateTabId): { snapshot: TabsSnapshot; tabId: string } | null {
  const [path, ...rest] = snapshot.closedPaths;
  if (!path) return null;
  const result = openTab({ ...snapshot, closedPaths: rest }, path, { activate: true, createId });
  return { snapshot: result.snapshot, tabId: result.tabId };
}

export function togglePinned(snapshot: TabsSnapshot, id: string): TabsSnapshot {
  if (!snapshot.tabs.some((tab) => tab.id === id)) return snapshot;
  const toggled = snapshot.tabs.map((tab) => (tab.id === id ? { ...tab, isPinned: !tab.isPinned } : tab));
  // Losgelöst: direkt hinter die verbleibenden angehefteten (Stelle 0 der übrigen).
  const loosened = toggled.find((tab) => tab.id === id && !tab.isPinned);
  if (!loosened) return { ...snapshot, tabs: pinnedFirst(toggled) };
  const rest = toggled.filter((tab) => tab.id !== id);
  const pinned = rest.filter((tab) => tab.isPinned);
  return { ...snapshot, tabs: [...pinned, loosened, ...rest.filter((tab) => !tab.isPinned)] };
}

/** Per Ziehen umsortieren — nur innerhalb derselben Zone (angeheftet / normal). */
export function moveTab(snapshot: TabsSnapshot, id: string, targetId: string, placeAfter: boolean): TabsSnapshot {
  const tab = snapshot.tabs.find((entry) => entry.id === id);
  const target = snapshot.tabs.find((entry) => entry.id === targetId);
  if (!tab || !target || tab === target || tab.isPinned !== target.isPinned) return snapshot;
  const rest = snapshot.tabs.filter((entry) => entry.id !== id);
  const index = rest.indexOf(target) + (placeAfter ? 1 : 0);
  return { ...snapshot, tabs: [...rest.slice(0, index), tab, ...rest.slice(index)] };
}

/** Titel oder Überschrift für den Tab dieses Pfads merken (unverändert → gleiches Objekt). */
export function setTabLabel(snapshot: TabsSnapshot, path: string, field: 'title' | 'heading', value: string): TabsSnapshot {
  const tab = findByKey(snapshot, path);
  if (!tab || tab[field] === value) return snapshot;
  return { ...snapshot, tabs: snapshot.tabs.map((entry) => (entry.id === tab.id ? { ...entry, [field]: value } : entry)) };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Gespeicherten Stand prüfen — alles Unbrauchbare fällt heraus, statt die App zu stören. */
export function parseSnapshot(raw: unknown): TabsSnapshot | null {
  if (!isRecord(raw) || !Array.isArray(raw.tabs)) return null;
  const tabs: OpenTab[] = [];
  for (const entry of raw.tabs) {
    if (!isRecord(entry) || typeof entry.id !== 'string' || typeof entry.path !== 'string' || !entry.path.startsWith('/')) continue;
    if (tabs.some((tab) => tab.id === entry.id || tabKey(tab.path) === tabKey(entry.path as string))) continue;
    const tab: OpenTab = { id: entry.id, path: entry.path, isPinned: entry.isPinned === true };
    if (typeof entry.title === 'string') tab.title = entry.title;
    if (typeof entry.heading === 'string') tab.heading = entry.heading;
    tabs.push(tab);
  }
  if (tabs.length === 0) return null;
  const activeId = tabs.some((tab) => tab.id === raw.activeId) ? (raw.activeId as string) : tabs[0].id;
  const closedPaths = Array.isArray(raw.closedPaths)
    ? raw.closedPaths.filter((path): path is string => typeof path === 'string' && path.startsWith('/')).slice(0, CLOSED_TABS_LIMIT)
    : [];
  return { tabs: pinnedFirst(tabs), activeId, closedPaths };
}

/* ---------- Store ---------- */

let idCounter = 0;
/** Eindeutig genug für eine Sitzung + gespeicherte Stände (Zeit + Zähler). */
export const createTabId: CreateTabId = () => `tab-${Date.now().toString(36)}-${(idCounter += 1).toString(36)}`;

export function tabsStorageKey(userId: string): string {
  return `proerp:open-tabs:${userId}`;
}

function loadSnapshot(userId: string): TabsSnapshot | null {
  try {
    const raw = localStorage.getItem(tabsStorageKey(userId));
    return raw ? parseSnapshot(JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

function storeSnapshot(userId: string, snapshot: TabsSnapshot): void {
  try {
    localStorage.setItem(tabsStorageKey(userId), JSON.stringify(snapshot));
  } catch {
    // Best effort — ohne Storage leben die Tabs nur bis zum Neuladen.
  }
}

interface OpenTabsState extends TabsSnapshot {
  /** Wessen Tabs geladen sind — vorher zeigt die Leiste nichts. */
  userId: string | null;
  /** Schon einmal abgeglichen? Die erste Adresse nach dem Laden ist ein Direktaufruf. */
  hasSynced: boolean;
  /** Kurz aufleuchtender Tab (im Hintergrund geöffnet oder schon offen); `seq` startet die Animation neu. */
  flash: { id: string; seq: number } | null;
  /** Letzter Hintergrund-Tab und sein Anker: Der nächste reiht sich dahinter ein, wie im Browser. */
  backgroundChain: { anchorId: string; lastId: string } | null;
  /**
   * Seiten mit ungespeicherten Änderungen (`tabKey`) — der Tab zeigt dafür einen
   * Punkt. Nur Anzeige, nicht gespeichert: Die Seite meldet es, solange sie offen ist.
   */
  dirtyKeys: string[];
  hydrate: (userId: string) => void;
  syncLocation: (path: string) => void;
  /** Neuen Stand übernehmen und speichern. */
  commit: (snapshot: TabsSnapshot) => void;
  openInBackground: (path: string) => void;
  /**
   * Tab für eine gleich folgende Navigation anlegen und seine ID zurückgeben —
   * navigieren muss der Aufrufer. Aktiv wird er erst, wenn die Adresse ankommt
   * (`syncLocation`): Lehnt die Seite das Verlassen ab (ungespeicherte
   * Änderungen), bleibt er im Hintergrund, statt als aktiv über einer anderen
   * Seite zu stehen.
   */
  prepareTab: (path: string) => string;
  /** Aufgeschobene Änderung (`deferTabChange`) übernehmen — die Navigation dazu ist angekommen. */
  applyTabChange: (token: string) => void;
  setLabel: (path: string, field: 'title' | 'heading', value: string) => void;
  setDirty: (path: string, isDirty: boolean) => void;
}

function snapshotOf(state: TabsSnapshot): TabsSnapshot {
  return { tabs: state.tabs, activeId: state.activeId, closedPaths: state.closedPaths };
}

export const useOpenTabs = create<OpenTabsState>((set, get) => {
  function commit(snapshot: TabsSnapshot) {
    const { userId } = get();
    const current = snapshotOf(get());
    if (snapshot.tabs === current.tabs && snapshot.activeId === current.activeId && snapshot.closedPaths === current.closedPaths) return;
    set(snapshot);
    if (userId) storeSnapshot(userId, snapshot);
  }
  return {
    tabs: [],
    activeId: null,
    closedPaths: [],
    userId: null,
    hasSynced: false,
    flash: null,
    backgroundChain: null,
    dirtyKeys: [],
    hydrate: (userId) => {
      if (get().userId === userId) return;
      const snapshot = loadSnapshot(userId) ?? createDefaultSnapshot(createTabId);
      set({ ...snapshot, userId, hasSynced: false, flash: null, backgroundChain: null, dirtyKeys: [] });
    },
    syncLocation: (path) => {
      const state = get();
      if (!state.userId || !isTabbablePath(path)) return;
      const next = syncLocation(snapshotOf(state), path, { isInitial: !state.hasSynced, createId: createTabId });
      if (!state.hasSynced) set({ hasSynced: true });
      if (next.activeId !== state.activeId) set({ backgroundChain: null });
      commit(next);
    },
    commit,
    openInBackground: (path) => {
      const state = get();
      if (!state.userId) return;
      const chain = state.backgroundChain;
      const afterId = chain && chain.anchorId === state.activeId && state.tabs.some((tab) => tab.id === chain.lastId) ? chain.lastId : undefined;
      const result = openTab(snapshotOf(state), path, { activate: false, afterId, createId: createTabId });
      commit(result.snapshot);
      set({
        flash: { id: result.tabId, seq: (state.flash?.seq ?? 0) + 1 },
        backgroundChain: result.isNew && state.activeId ? { anchorId: state.activeId, lastId: result.tabId } : state.backgroundChain,
      });
    },
    prepareTab: (path) => {
      const state = get();
      const result = openTab(snapshotOf(state), path, { activate: false, createId: createTabId });
      set({ backgroundChain: null });
      commit(result.snapshot);
      return result.tabId;
    },
    applyTabChange: (token) => {
      const change = takeDeferredTabChange(token);
      if (change) commit(change(snapshotOf(get())));
    },
    setLabel: (path, field, value) => {
      const state = get();
      commit(setTabLabel(snapshotOf(state), path, field, value));
    },
    setDirty: (path, isDirty) => {
      const key = tabKey(path);
      const { dirtyKeys } = get();
      if (dirtyKeys.includes(key) === isDirty) return;
      set({ dirtyKeys: isDirty ? [...dirtyKeys, key] : dirtyKeys.filter((entry) => entry !== key) });
    },
  };
});

/** Hat die Seite dieser Adresse ungespeicherte Änderungen? */
export function isTabDirty(path: string): boolean {
  return useOpenTabs.getState().dirtyKeys.includes(tabKey(path));
}

/**
 * Ungespeicherte Änderungen der Seite am Tab anzeigen (Punkt statt ×). Beim
 * Verlassen der Seite verschwindet die Markierung von selbst.
 */
export function useTabDirty(isDirty: boolean, path: string): void {
  const setDirty = useOpenTabs((state) => state.setDirty);
  useEffect(() => {
    setDirty(path, isDirty);
    return () => setDirty(path, false);
  }, [path, isDirty, setDirty]);
}

/* ---------- Tab-Änderungen, die auf die Navigation warten ---------- */

/**
 * Eine Änderung an den Tabs, die erst gilt, wenn die Navigation dazu ankommt.
 *
 * Schließen des aktiven Tabs heißt: zum Nachbarn navigieren. Hat die Seite
 * ungespeicherte Änderungen, kann sie das ablehnen (Rückfrage, „Abbrechen") —
 * wäre der Tab dann schon weg, stünde der Editor ohne Tab da. Deshalb reist die
 * Änderung als Marke im Router-State mit (`TabSwitchState.tabChange`) und
 * `useTabSync` übernimmt sie beim Ankommen. Eine abgebrochene Navigation kommt
 * nie an und ändert nichts. Die Änderung selbst bleibt im Speicher, nicht im
 * Verlauf: Zurück/Vor auf diesen Eintrag oder Neuladen wendet sie nicht erneut an.
 */
export type TabChange = (snapshot: TabsSnapshot) => TabsSnapshot;

const deferredTabChanges = new Map<string, TabChange>();
/** Abgebrochene Navigationen kommen nie an — nur die jüngsten Änderungen aufheben. */
const DEFERRED_TAB_CHANGES_LIMIT = 10;
let deferredCounter = 0;

export function deferTabChange(change: TabChange): string {
  const token = `change-${Date.now().toString(36)}-${(deferredCounter += 1).toString(36)}`;
  deferredTabChanges.set(token, change);
  while (deferredTabChanges.size > DEFERRED_TAB_CHANGES_LIMIT) {
    const oldest = deferredTabChanges.keys().next();
    if (oldest.done) break;
    deferredTabChanges.delete(oldest.value);
  }
  return token;
}

/** Aufgeschobene Änderung holen — genau einmal. */
export function takeDeferredTabChange(token: string): TabChange | undefined {
  const change = deferredTabChanges.get(token);
  deferredTabChanges.delete(token);
  return change;
}

/* ---------- Scrollposition je Tab ---------- */

/** Scrollposition je Tab (nicht gespeichert — überlebt Tab-Wechsel, nicht das Neuladen). */
const tabScrollPositions = new Map<string, number>();

export function rememberTabScroll(tabId: string, top: number): void {
  tabScrollPositions.set(tabId, top);
}

export function tabScrollOf(tabId: string): number | undefined {
  return tabScrollPositions.get(tabId);
}

/** Router-State eines Tab-Wechsels — die App-Hülle stellt damit die Scrollposition wieder her. */
export interface TabSwitchState {
  tabSwitch: string;
  /** Marke einer Tab-Änderung, die mit dieser Navigation gilt (`deferTabChange`). */
  tabChange?: string;
}

export function tabSwitchIdOf(state: unknown): string | null {
  return isRecord(state) && typeof state.tabSwitch === 'string' ? state.tabSwitch : null;
}

export function tabChangeOf(state: unknown): string | null {
  return isRecord(state) && typeof state.tabChange === 'string' ? state.tabChange : null;
}

/**
 * Titel des aktuellen Tabs melden, sobald die Seite ihn kennt (`null` = noch
 * nicht geladen). Brotkrumen-Seiten tun das automatisch; eigene Seiten nur,
 * wenn ihre Überschrift als Tab-Titel nicht taugt.
 *
 * `path`: Tab einer anderen Adresse beschriften — der Vollbild-Editor
 * (`/verkauf/:id/editor`) meldet so den Titel für den Tab seines Belegs
 * (`/verkauf/:id`), falls es ihn gibt.
 */
export function useTabTitle(title: string | null | undefined, path?: string): void {
  const { pathname } = useLocation();
  const target = path ?? pathname;
  const setLabel = useOpenTabs((state) => state.setLabel);
  useEffect(() => {
    if (title) setLabel(target, 'title', title);
  }, [target, title, setLabel]);
}
